import { useState, useEffect, useRef } from "react";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategorySelect = (category: typeof categories[0]) => {
    setSelectedCategory(category.name);
    setIsDropdownOpen(false);
    window.open(category.url, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="product-categories" className="w-full py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            Browse Product Categories
          </h2>
          <p className="mt-3 text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Explore our wide range of product categories on <strong>GTBuy Sheet</strong> &mdash; from footwear to accessories, find everything you need.
          </p>
        </div>

        <div className="flex justify-center mb-10" ref={dropdownRef}>
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-100 border border-background-200 rounded-lg text-foreground-800 text-sm cursor-pointer hover:border-primary-300 transition-colors duration-200"
            >
              <span className={selectedCategory ? "text-foreground-900" : "text-foreground-500"}>
                {selectedCategory || "Select a product category..."}
              </span>
              <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat)}
                    className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer first:rounded-t-lg last:rounded-b-lg"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="md:hidden overflow-x-auto -mx-4 px-4 pb-2">
          <div className="flex gap-3 w-max">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex-shrink-0 w-[140px] group rounded-xl bg-background-100 border border-background-200 overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
              >
                <div className="relative w-full h-[100px] overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="px-3 py-2.5 text-center">
                  <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-700 whitespace-nowrap transition-colors">
                    {cat.name}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="hidden md:grid grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group rounded-xl bg-background-100 border border-background-200 overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer hover:-translate-y-1"
            >
              <div className="relative w-full h-[130px] overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  loading="lazy"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="px-3 py-3 text-center">
                <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-700 whitespace-nowrap transition-colors">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}